<template>
    <Editor :api-key="apiKey" v-model="localContent" :init="options" :model-events="'input change keyup undo redo'" />
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import DOMPurify from 'dompurify';

export default {
    components: { Editor },
    props: {
        modelValue: { type: String, default: '' },
        height: { type: String, default: '400' }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            isSyncing: false,
            apiKey: '1ao49xzr4fiwtdklq4bvs6pityzlwuup1cugidkn5d3tf65n',
            localContent: this.modelValue,
            options: {
                plugins: 'preview paste importcss searchreplace autolink directionality visualblocks visualchars fullscreen link codesample table hr pagebreak nonbreaking advlist lists wordcount textpattern noneditable charmap quickbars emoticons code image',
                toolbar: 'bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor backcolor removeformat | pagebreak image table | charmap emoticons | fullscreen preview | link codesample code',
                // barre d’outils spécifique au tableau
                table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablecellprops tablerowprops',
                // autoriser les balises/attributs de tableau
                valid_elements:
                    'p[style],strong[style],em[style],ul[style],ol[style],li[style],' +
                    'a[href|target|style],h1[style],h2[style],h3[style],h4[style],h5[style],h6[style],' +
                    'span[class|style],br,hr[class|style],img[class|src|alt|width|height|style|srcset|sizes],' +
                    // tableaux :
                    'table[class|style|width|height|border|cellpadding|cellspacing],' +
                    'thead[class|style],tbody[class|style],tfoot[class|style],tr[class|style],' +
                    'th[class|style|rowspan|colspan|width|height|align|valign],' +
                    'td[class|style|rowspan|colspan|width|height|align|valign]',
                extended_valid_elements:
                    'table[class|style|width|height|border|cellpadding|cellspacing];' +
                    'thead[class|style];tbody[class|style];tfoot[class|style];tr[class|style];' +
                    'th[class|style|rowspan|colspan|width|height|align|valign];' +
                    'td[class|style|rowspan|colspan|width|height|align|valign]',
                content_style:
                    'body { font-family:Montserrat,Arial,sans-serif; font-size:11pt; line-height:1.5 }' +

                    /* Titres */
                    'h1 { font-family:"Montserrat Alternates SemiBold"; font-size:32pt; color:#1F2B3D; margin:7.5pt 0 }' +
                    'h2 { font-family:"Montserrat Alternates SemiBold"; font-size:29pt; color:#296A9D; margin:14pt 0 }' +
                    'h3 { font-family:"Montserrat Alternates SemiBold"; font-size:26pt; color:#F4706A; margin:14pt 0 }' +
                    'h4 { font-family:"Montserrat Alternates SemiBold"; font-size:18pt; color:#F4706A; margin:12pt 0 }' +
                    'h5 { font-family:"Montserrat Alternates SemiBold"; font-size:14pt; color:#296A9D; margin:12pt 0; border-bottom:3pt solid #F4706A; padding-bottom:3pt }' +
                    'h6 { font-family:"Montserrat Alternates SemiBold"; font-size:14pt; color:#296A9D; margin:8pt 0 }' +

                    /* Gras (elevate) */
                    'strong, b { font-family:"Montserrat Alternates SemiBold"; font-size:11pt; color:#296A9D }' +

                    /* Tables */
                    'table { border-collapse:collapse; width:100%; table-layout:fixed }' +
                    'table td, table th { border:1px solid #BA9E66; padding:4pt 6pt; font-family:Montserrat,Arial,sans-serif; font-size:9pt; vertical-align:middle; word-break:break-word }' +
                    'thead th, tr th, thead td { background-color:#296A9D; color:#FFFFFF; text-align:center; font-family:"Montserrat Alternates SemiBold" }' +
                    'tbody td:first-child { text-align:left }' +
                    'tbody td:not(:first-child) { text-align:center }' +

                    /* Images */
                    'img { max-width:100%; height:auto }' +

                    /* Pagebreak */
                    'hr.mce-pagebreak { border:0; border-top:1px dashed #ccc; margin:16px 0 }' +
                    '@media print { hr.mce-pagebreak { break-before:page; page-break-before:always } }',
                paste_data_images: true,
                automatic_uploads: false,
                menubar: 'edit view insert format tools table help',
                invalid_elements: 'script,iframe,object,embed,style',
                height: parseInt(this.height, 10),
                image_caption: true,
                quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
                noneditable_noneditable_class: 'mceNonEditable',
                toolbar_mode: 'sliding',
                font_formats: 'Montserrat Alternates SemiBold=Montserrat Alternates SemiBold; Montserrat="Montserrat"',

                /* Optionnel : évite des HR imbriqués si on insère au milieu d’un <p> */
                pagebreak_separator: '<hr class="mce-pagebreak" />',
                pagebreak_split_block: true,
                image_dimensions: true, // garde width/height sur la balise
                resize_img_proportional: true, // maintien le ratio au redimensionnement
            }
        };
    },
    watch: {
        modelValue(newVal) {
            // Ne pousse dans l'éditeur QUE si ce n’est pas une MAJ que TU viens d’émettre
            if (this.isSyncing) return;
            if (newVal !== this.localContent) this.localContent = newVal;
        },
        localContent(newVal) {
            if (this.isSyncing) return;

            const purified = DOMPurify.sanitize(newVal, {
                ADD_ATTR: ['style', 'class', 'srcset', 'sizes', 'rowspan', 'colspan', 'width', 'height', 'align', 'valign', 'border', 'cellpadding', 'cellspacing'],
                ALLOWED_TAGS: ['p', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'br', 'hr', 'img', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td'],
                ALLOWED_ATTR: ['href', 'target', 'style', 'class', 'src', 'alt', 'width', 'height', 'srcset', 'sizes', 'rowspan', 'colspan', 'align', 'valign', 'border', 'cellpadding', 'cellspacing'],
                ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/[a-z0-9.+-]+(?:;[a-z0-9.=+-]+)?,|blob:))/i,
                ADD_DATA_URI_TAGS: ['img'],
            });

            // Ne touche aux <hr> que si nécessaire, pour éviter des diffs inutiles
            let finalHtml = purified;
            if (purified.includes('<hr')) {
                const wrapper = document.createElement('div');
                wrapper.innerHTML = purified;
                wrapper.querySelectorAll('hr').forEach(hr => {
                    if (!hr.classList.contains('mce-pagebreak')) hr.className = 'mce-pagebreak';
                });
                finalHtml = wrapper.innerHTML;
            }

            if (finalHtml !== this.modelValue) {
                this.isSyncing = true;                     // <-- on entre en phase « émission »
                this.$emit('update:modelValue', finalHtml);
                this.$nextTick(() => { this.isSyncing = false; }); // <-- fin de la phase
            }
        }
    }
};
</script>
